import { utils, writeFile } from 'xlsx';

const FALLBACK_LABELS = {
  user: 'مستخدم غير معروف',
  school: 'مدرسة غير معروفة',
  file: 'تصدير',
};

const DEFAULT_PASSWORD = 'alameed';

const sanitizeFileName = (value) =>
  (value || FALLBACK_LABELS.file)
    .toString()
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 80) || FALLBACK_LABELS.file;

const resolveUserId = (user) =>
  user?.userIdNumber ||
  user?.userId ||
  user?.userCode ||
  user?.nationalId ||
  user?._id ||
  '';

const resolveUserName = (user) =>
  user?.userName ||
  user?.fullName ||
  user?.name ||
  user?.studentName ||
  resolveUserId(user) ||
  FALLBACK_LABELS.user;

const resolveNamePartsFromFull = (fullName) => {
  if (!fullName) {
    return ['', ''];
  }
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) {
    return [parts[0], parts[0]];
  }
  return [parts[0], parts.slice(1).join(' ')];
};

const resolveFirstName = (user) =>
  user?.firstName ||
  user?.FirstName ||
  user?.first_name ||
  user?.studentFirstName ||
  resolveNamePartsFromFull(resolveUserName(user))[0] ||
  '';

const resolveLastName = (user) =>
  user?.lastName ||
  user?.LastName ||
  user?.last_name ||
  user?.studentLastName ||
  resolveNamePartsFromFull(resolveUserName(user))[1] ||
  '';

const resolveEmailAddress = (user) =>
  user?.email ||
  user?.Email ||
  user?.emailAddress ||
  user?.email_address ||
  user?.studentEmail ||
  user?.userEmail ||
  user?.login ||
  '';

const buildAgEmailFromIdentifier = (identifier) => {
  if (!identifier) {
    return '';
  }
  return `${String(identifier).trim().toLowerCase()}@ag.edu.iq`;
};

const resolveUserImage = (user) =>
  user?.imageUrl ||
  user?.imageURL ||
  user?.image ||
  user?.photo ||
  user?.photoUrl ||
  user?.avatar ||
  user?.userImage ||
  user?.studentImage ||
  '';

const resolveSchoolName = (school, user) =>
  school?.schoolName ||
  school?.name ||
  school?.SchoolName ||
  user?.schoolName ||
  user?.school ||
  FALLBACK_LABELS.school;

const buildPrintingRow = (user, school) => {
  const schoolName = resolveSchoolName(school, user);
  return {
    المعرف: resolveUserId(user),
    'اسم المستخدم': resolveUserName(user),
    'اسم المدرسة': schoolName,
    'صورة المستخدم': resolveUserImage(user),
  };
};

const buildAddEmailRow = (user) => ({
  'First Name [Required]': resolveFirstName(user),
  'Last Name [Required]': resolveLastName(user),
  'Email Address [Required]':
    buildAgEmailFromIdentifier(resolveUserId(user)) || resolveEmailAddress(user),
  'Password [Required]': DEFAULT_PASSWORD,
});

const buildPaperCatRow = (user, school) => {
  const identifier = resolveUserId(user);
  return {
    Username: identifier,
    'Full Name': resolveUserName(user),
    Email: buildAgEmailFromIdentifier(identifier),
    Department: resolveSchoolName(school, user),
    'Primary Card Number': user?.nfcId || '',
  };
};

const EXPORT_VARIANTS = {
  printing: {
    builder: buildPrintingRow,
    fileSuffix: 'printing',
    sheetName: 'الطباعة',
  },
  'add-email': {
    builder: buildAddEmailRow,
    fileSuffix: 'add-email',
    sheetName: 'إضافة البريد',
  },
  'paper-cat': {
    builder: buildPaperCatRow,
    fileSuffix: 'paper-cat',
    sheetName: 'papercut',
  },
};

const exportRows = (rows, { fileName, sheetName = 'البيانات' }) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return false;
  }

  const worksheet = utils.json_to_sheet(rows);
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, sheetName);
  writeFile(workbook, `${sanitizeFileName(fileName)}.xlsx`);
  return true;
};

const resolveVariantConfig = (variant) =>
  EXPORT_VARIANTS[variant] || EXPORT_VARIANTS.printing;

export const exportSchoolUsersToExcel = ({
  users = [],
  school,
  variant = 'printing',
}) => {
  const config = resolveVariantConfig(variant);
  const rows = users.map((user) => config.builder(user, school));
  const schoolName = resolveSchoolName(school);
  return exportRows(rows, {
    fileName: `${schoolName}-${config.fileSuffix}`,
    sheetName: config.sheetName,
  });
};

export const exportSingleUserToExcel = ({
  user,
  school,
  variant = 'printing',
}) => {
  if (!user) {
    return false;
  }
  const config = resolveVariantConfig(variant);
  const row = config.builder(user, school);
  const userName = resolveUserName(user);
  return exportRows([row], {
    fileName: `${userName}-${config.fileSuffix}`,
    sheetName: config.sheetName,
  });
};

export const exportCustomRows = exportRows;

